import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";
import { CONTACT, waLink, WHATSAPP_GENERIC } from "@/data/content";

const METHODS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: CONTACT.phoneDisplay,
    href: waLink(WHATSAPP_GENERIC),
    external: true,
    tid: "contact-whatsapp",
  },
  {
    icon: Phone,
    label: "Phone",
    value: CONTACT.phoneDisplay,
    href: CONTACT.phoneTel,
    external: false,
    tid: "contact-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
    external: false,
    tid: "contact-email",
  },
  {
    icon: MapPin,
    label: "Address",
    value: CONTACT.address,
    href: null,
    external: false,
    tid: "contact-address",
  },
];

export default function Contact() {
  return (
    <section className="pt-[72px] pb-14 md:pb-20 bg-slate-50 min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="eyebrow"
        >
          Contact
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
          className="mt-3 font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-navy leading-[1.02]"
        >
          Get in Touch
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
          className="mt-4 max-w-xl text-base text-slate-600 leading-relaxed"
        >
          Tell us what your team or business needs. The more detail you share — sport,
          products, colors, quantities — the sharper our first answer.
        </motion.p>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-4">
            {METHODS.map((m, i) => {
              const inner = (
                <>
                  <span className="shrink-0 rounded-md bg-icelight p-3">
                    <m.icon size={19} className="text-icehover" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{m.label}</span>
                    <span className="block mt-0.5 text-sm font-semibold text-navy">{m.value}</span>
                  </span>
                </>
              );
              const cls =
                "flex items-center gap-4 bg-white rounded-lg border border-slate-200 p-5 hover:border-ice/60 hover:shadow-sm transition-all duration-200";
              return (
                <Reveal key={m.label} delay={i * 0.06}>
                  {m.href ? (
                    <a
                      href={m.href}
                      {...(m.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      data-testid={m.tid}
                      className={cls}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={m.tid} className={cls}>
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
            <Reveal delay={0.25}>
              <p className="text-sm text-slate-500 leading-relaxed pt-2">
                Prefer to talk it through? WhatsApp is the fastest way to reach us — send
                your design, logo or reference directly in the chat.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 sm:p-9">
              <QuoteForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
